import { useState } from 'react'
import styled from 'styled-components'
import { toast } from 'react-toastify'
import GavelRoundedIcon from '@mui/icons-material/GavelRounded'
import ApartmentRoundedIcon from '@mui/icons-material/ApartmentRounded'
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded'
import IncidentsAPI from 'services/IncidentsAPI'
import CompleteReportModal from './CompleteReportModal'

const IncidentReportsSection = ({ incident, onUpdated }) => {
  const [busy, setBusy] = useState(false)
  const [reportType, setReportType] = useState(null)
  const isHrb = !!incident?.buildingPassport?.isHrb
  const bpName = incident?.buildingPassport?.name || 'this building'
  const riddorOn = !!incident?.riddorReportable
  const morOn = !!incident?.morReportable
  const riddorDone = !!incident?.reportDetails?.riddor?.completedAt
  const morDone = !!incident?.reportDetails?.mor?.completedAt

  const setReportable = async (field, value) => {
    setBusy(true)
    try {
      await IncidentsAPI.update(incident.id, { [field]: value })
      onUpdated?.()
    } catch (e) {
      toast.error('Could not update the reportability. Please try again.')
    } finally {
      setBusy(false)
    }
  }

  const markHrb = async () => {
    setBusy(true)
    try {
      await IncidentsAPI.markBuildingHrb(incident.id)
      toast.success(`${bpName} is now marked as a Higher-Risk Building.`)
      onUpdated?.()
    } catch (e) {
      toast.error('Could not update the building. Please try again.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <Wrap>
      <Label>Regulatory reports</Label>
      <ToggleRow>
        <Toggle type="button" $on={riddorOn} onClick={() => setReportable('riddor_reportable', !riddorOn)} disabled={busy}>
          <Check $on={riddorOn} /> RIDDOR reportable
        </Toggle>
        {isHrb && (
          <Toggle type="button" $on={morOn} $mor onClick={() => setReportable('mor_reportable', !morOn)} disabled={busy}>
            <Check $on={morOn} $mor /> MOR reportable
          </Toggle>
        )}
      </ToggleRow>
      <Row>
        <ReportButton type="button" onClick={() => setReportType('riddor')} disabled={busy}>
          <GavelRoundedIcon style={{ fontSize: 15 }} />
          {riddorDone ? 'Update RIDDOR record' : 'Complete RIDDOR record'}
          {riddorDone && <DoneTick><CheckCircleRoundedIcon style={{ fontSize: 14 }} /></DoneTick>}
        </ReportButton>
        {isHrb && (
          <ReportButton type="button" $mor onClick={() => setReportType('mor')} disabled={busy}>
            <ApartmentRoundedIcon style={{ fontSize: 15 }} />
            {morDone ? 'Update MOR record' : 'Complete MOR record'}
            {morDone && <DoneTick $mor><CheckCircleRoundedIcon style={{ fontSize: 14 }} /></DoneTick>}
          </ReportButton>
        )}
      </Row>
      {!isHrb && (
        <HrbPrompt>
          Mandatory Occurrence Reporting only applies to Higher-Risk Buildings. If {bpName} is an HRB,{' '}
          <LinkButton type="button" onClick={markHrb} disabled={busy}>mark it as a Higher-Risk Building</LinkButton>{' '}
          to enable a MOR report.
        </HrbPrompt>
      )}
      <Hint>
        You fill in the report here, then download a finished Word document — pre-filled from this incident
        and the tasks raised from it, with nothing left blank. Submit it through the{' '}
        {isHrb ? 'HSE (RIDDOR) or Building Safety Regulator (MOR)' : 'HSE'} online service.
      </Hint>
      <CompleteReportModal
        open={!!reportType}
        type={reportType}
        incident={incident}
        onClose={() => setReportType(null)}
        onSaved={onUpdated}
      />
    </Wrap>
  )
}

export default IncidentReportsSection

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const Label = styled.div`
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.grey?.[800] || '#64748b'};
`

const Row = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
`

const ToggleRow = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`

const Toggle = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 7px 12px;
  border-radius: 999px;
  border: 1px solid ${({ $on, $mor }) => ($on ? ($mor ? '#c2410c' : '#a16207') : 'rgba(12, 18, 41, 0.14)')};
  background: ${({ $on, $mor }) => ($on ? ($mor ? '#ffedd5' : '#fef3c7') : '#fff')};
  color: ${({ $on, $mor, theme }) => ($on ? ($mor ? '#9a3412' : '#92400e') : (theme.colors.grey?.[800] || '#64748b'))};
  font-size: 12.5px;
  font-weight: 700;
  cursor: pointer;
  &:disabled { opacity: 0.6; cursor: default; }
`

const Check = styled.span`
  width: 13px;
  height: 13px;
  border-radius: 3px;
  flex: 0 0 auto;
  border: 1.5px solid ${({ $on, $mor }) => ($on ? ($mor ? '#c2410c' : '#a16207') : '#9ca3af')};
  background: ${({ $on, $mor }) => ($on ? ($mor ? '#c2410c' : '#d97706') : 'transparent')};
`

const ReportButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 9px 14px;
  border-radius: 8px;
  border: 1px solid ${({ $mor }) => ($mor ? 'rgba(194, 65, 12, 0.35)' : 'rgba(161, 98, 7, 0.35)')};
  background: ${({ $mor }) => ($mor ? '#fff5f0' : '#fffbeb')};
  color: ${({ $mor }) => ($mor ? '#9a3412' : '#92400e')};
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  &:hover { filter: brightness(0.98); }
  &:disabled { opacity: 0.6; cursor: default; }
`

const DoneTick = styled.span`
  display: inline-flex;
  align-items: center;
  margin-left: 2px;
  color: #15803d;
`

const HrbPrompt = styled.div`
  font-size: 12.5px;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.grey?.[800] || '#64748b'};
  background: #f8fafc;
  border: 1px solid rgba(12, 18, 41, 0.08);
  border-radius: 8px;
  padding: 9px 12px;
`

const LinkButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  font: inherit;
  font-weight: 700;
  color: #c2410c;
  cursor: pointer;
  text-decoration: underline;
  &:disabled { opacity: 0.6; cursor: default; }
`

const Hint = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.grey?.[800] || '#64748b'};
`
